import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Checkbox, IconButton, InputAdornment, Tooltip } from '@mui/material';
import { useLocalStorage } from 'services/hooks/useLocalStorage';
import { SearchContainer, StyledTextField } from './ExpandableSearch.styles';

export interface ExpandableSearchHandle {
  expand: () => void;
}

interface ExpandableSearchProps {
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  isValuationsPage?: boolean;
  /** When provided with onSearchAllChange, parent controls "search all" (for API). */
  searchAll?: boolean;
  onSearchAllChange?: (value: boolean) => void;
}

export const ExpandableSearch = forwardRef<ExpandableSearchHandle, ExpandableSearchProps>(({
  value,
  onChange,
  placeholder = 'Search...',
  isValuationsPage = false,
  searchAll: searchAllProp,
  onSearchAllChange,
}, ref) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchAllLocal, setSearchAllLocal] = useLocalStorage('searchAll', false);
  const isControlled = searchAllProp !== undefined && onSearchAllChange !== undefined;
  const searchAll = isControlled ? searchAllProp : searchAllLocal;
  const setSearchAll = isControlled ? onSearchAllChange! : setSearchAllLocal;
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleExpand = () => {
    setIsExpanded(true);
    // Focus input after expansion animation starts
    setTimeout(() => {
      inputRef.current?.focus();
    }, 50);
  };

  useImperativeHandle(ref, () => ({
    expand: handleExpand,
  }));

  const handleClear = () => {
    // Create synthetic event for onChange
    const syntheticEvent = {
      target: { value: '' },
    } as React.ChangeEvent<HTMLInputElement>;
    onChange(syntheticEvent);
    inputRef.current?.focus();
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!value) {
      const relatedTarget = e.relatedTarget as Node | null;
      if (!containerRef.current?.contains(relatedTarget)) {
        setIsExpanded(false);
      }
    }
  };

  return (
    <SearchContainer isExpanded={isExpanded}>
      {isExpanded ? (
        <Box ref={containerRef} display="flex" width="100%" alignItems="center">
          <StyledTextField
            size="small"
            variant="outlined"
            inputRef={inputRef}
            value={value}
            onChange={onChange}
            onBlur={handleBlur}
            placeholder={placeholder}
            fullWidth
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: 'action.active' }} />
                  </InputAdornment>
                ),
                endAdornment: value ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={handleClear}
                      onMouseDown={e => e.preventDefault()}
                      edge="end"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              },
            }}
          />
          {isValuationsPage && (
            <Tooltip title="Search All Valuations" arrow>
              <Checkbox
                checked={searchAll}
                onChange={() => setSearchAll(!searchAll)}
                inputProps={{ 'aria-label': 'controlled', 'aria-describedby': 'Full Search Toggle' }}
              />
            </Tooltip>
          )}
        </Box>
      ) : (
        <Tooltip title="Search by company name" arrow>
          <IconButton onClick={handleExpand} size="small">
            <SearchIcon />
          </IconButton>
        </Tooltip>
      )}
    </SearchContainer>
  );
});
